import React, { useEffect, useState } from 'react';
import { BarChart3, TrendingUp, TrendingDown, Loader2, MessageCircle } from 'lucide-react';
import { toast } from 'react-toastify';
import ModernSettingHeader from '@/components/settings/ModernSettingHeader';
import {
  whatsappReportService,
  WhatsAppReport,
} from '@/services/integrations/whatsappReportService';

const RANGE_OPTIONS = [
  { days: 7, label: 'Last 7 days' },
  { days: 30, label: 'Last 30 days' },
  { days: 90, label: 'Last 90 days' },
];

/** "4m 12s" / "1h 6m" / "3d 2h" — never a raw second count. */
const formatDuration = (seconds: number | null): string => {
  if (seconds == null) return '—';
  const s = Math.round(seconds);
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ${s % 60}s`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ${m % 60}m`;
  const d = Math.floor(h / 24);
  return `${d}d ${h % 24}h`;
};

const Tile: React.FC<{ label: string; value: string; sub?: React.ReactNode; tone?: 'default' | 'danger' }> = ({
  label, value, sub, tone = 'default',
}) => (
  <div className={`bg-white rounded-lg border p-4 ${tone === 'danger' ? 'border-red-200' : 'border-gray-200'}`}>
    <div className="text-xs font-medium text-gray-500 mb-1.5">{label}</div>
    <div className={`text-2xl font-bold ${tone === 'danger' ? 'text-red-600' : 'text-gray-900'}`}>{value}</div>
    {sub && <div className="text-xs text-gray-500 mt-1">{sub}</div>}
  </div>
);

const WhatsAppReportPage: React.FC = () => {
  const [days, setDays] = useState(30);
  const [report, setReport] = useState<WhatsAppReport | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    whatsappReportService.getReport(days)
      .then((r) => { if (!cancelled) setReport(r); })
      .catch((e) => {
        console.error('Failed to load WhatsApp report', e);
        toast.error('Failed to load WhatsApp performance report');
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [days]);

  const s = report?.summary;

  return (
    <div className="w-full">
      <ModernSettingHeader
        title="WhatsApp Reporting"
        description="Derived entirely from stored message, conversation and order rows — no separate analytics pipeline."
        icon={<BarChart3 className="w-6 h-6" />}
      />

      <div className="p-6 space-y-6">
        {/* Range picker */}
        <div className="flex items-center gap-2">
          {RANGE_OPTIONS.map((opt) => (
            <button
              key={opt.days}
              onClick={() => setDays(opt.days)}
              className={`px-3 py-1.5 text-sm rounded-lg border ${
                days === opt.days
                  ? 'border-brandColor text-brandColor bg-green-50 font-medium'
                  : 'border-gray-200 text-gray-600 hover:bg-gray-50'
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="text-center py-16">
            <Loader2 className="w-8 h-8 animate-spin mx-auto text-brandColor" />
            <p className="mt-4 text-gray-500">Crunching the numbers…</p>
          </div>
        ) : !report || !s ? (
          <div className="text-center py-16 bg-white rounded-lg border border-gray-200">
            <MessageCircle className="w-14 h-14 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500">No data yet for this period.</p>
          </div>
        ) : (
          <>
            {/* Top-line tiles */}
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
              <Tile
                label="Conversations"
                value={String(s.conversations)}
                sub={s.conversations_delta_pct != null ? (
                  <span className={`flex items-center gap-1 font-medium ${s.conversations_delta_pct >= 0 ? 'text-green-700' : 'text-red-600'}`}>
                    {s.conversations_delta_pct >= 0 ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
                    {s.conversations_delta_pct >= 0 ? '+' : ''}{s.conversations_delta_pct}% vs prev
                  </span>
                ) : 'no prior-period data'}
              />
              <Tile label="Incoming" value={String(s.incoming)} sub={`${s.conversations ? (s.incoming / s.conversations).toFixed(1) : '0'} per conversation`} />
              <Tile label="Outgoing" value={String(s.outgoing)} sub={`${s.outgoing_templates} templates`} />
              <Tile label="Delivered" value={s.delivered_pct != null ? `${s.delivered_pct}%` : '—'} sub={`${s.delivered} of ${s.outgoing}`} />
              <Tile label="Read" value={s.read_pct_of_delivered != null ? `${s.read_pct_of_delivered}%` : '—'} sub="of delivered" />
              <Tile
                label="Failed"
                value={String(s.failed)}
                tone={s.failed > 0 ? 'danger' : 'default'}
                sub={s.failed_pct != null
                  ? `${s.failed_pct}%${s.failed_outside_window > 0 ? ` · ${s.failed_outside_window} outside window` : ''}`
                  : undefined}
              />
            </div>

            {/* Response / resolution */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <Tile
                label="Median first response"
                value={formatDuration(report.median_first_response_seconds)}
                sub={`${report.conversations_with_reply} conversation(s) with a reply`}
              />
              <Tile
                label="Median resolution"
                value={formatDuration(report.median_resolution_seconds)}
                sub={`${report.resolved_count} resolved in period · open→resolved, approximate`}
              />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-[1.6fr_1fr] gap-6">
              {/* By agent */}
              <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                <div className="px-5 py-3 border-b border-gray-100">
                  <h3 className="text-sm font-semibold text-gray-900">By agent</h3>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-gray-50 text-gray-500 text-[11px] uppercase tracking-wide">
                        <th className="text-left font-semibold px-5 py-2">Agent</th>
                        <th className="text-right font-semibold px-3 py-2">Convs</th>
                        <th className="text-right font-semibold px-3 py-2">Msgs</th>
                        <th className="text-right font-semibold px-3 py-2">1st response</th>
                        <th className="text-right font-semibold px-5 py-2">Resolved</th>
                      </tr>
                    </thead>
                    <tbody>
                      {report.by_agent.length === 0 ? (
                        <tr><td colSpan={5} className="text-center text-gray-400 py-8">No assigned conversations in this period.</td></tr>
                      ) : (
                        report.by_agent.map((a) => (
                          <tr key={a.user_id} className="border-t border-gray-50">
                            <td className="px-5 py-2.5 font-medium text-gray-900">{a.user_name}</td>
                            <td className="text-right px-3 py-2.5 font-medium">{a.conversations}</td>
                            <td className="text-right px-3 py-2.5 text-gray-600">{a.messages}</td>
                            <td className="text-right px-3 py-2.5 text-gray-600">{formatDuration(a.median_first_response_seconds)}</td>
                            <td className="text-right px-5 py-2.5 text-gray-600">{a.resolved}</td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Why messages failed */}
              <div className="bg-white rounded-lg border border-gray-200 p-5">
                <h3 className="text-sm font-semibold text-gray-900 mb-4">Why messages failed</h3>
                {report.failure_breakdown.length === 0 ? (
                  <p className="text-sm text-gray-400">No failed sends in this period.</p>
                ) : (
                  <div className="space-y-3">
                    {report.failure_breakdown.map((f, i) => {
                      const max = Math.max(...report.failure_breakdown.map((x) => x.count));
                      return (
                        <div key={i} className="flex items-center gap-3">
                          <div className="w-32 text-xs font-medium text-gray-600 truncate" title={f.label}>{f.label}</div>
                          <div className="flex-1 h-2.5 rounded bg-gray-100 overflow-hidden">
                            <div className="h-full rounded bg-amber-500" style={{ width: `${(f.count / max) * 100}%` }} />
                          </div>
                          <div className="w-8 text-right text-xs font-semibold">{f.count}</div>
                        </div>
                      );
                    })}
                  </div>
                )}
                <p className="text-[11px] text-gray-400 mt-4 pt-4 border-t border-gray-100">
                  Grouped by Meta's error code on failed outbound messages. A cart-recovery funnel isn't shown here yet —
                  there's no tagged concept of "this template is a cart reminder" in the data model to build one on honestly.
                </p>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default WhatsAppReportPage;
